import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { applyStoredTheme, THEME_STORAGE_KEY } from './theme-script.js';
const ThemeContext = React.createContext(undefined);
/**
 * Internal portal ONLY (§8.1/§8.8). Never mount this in the public app — use
 * `PublicThemeScope` instead, which is permanently light and carries none of this
 * provider's dark-mode plumbing.
 *
 * Persistence model per §8.8: mirrored to localStorage for instant same-tab
 * application; the authoritative copy lives on the staff account server-side and is
 * reconciled by the consuming app (this provider does not make network calls — that
 * would violate this package's zero-backend-dependency constraint).
 */
export function ThemeProvider({ children, onPreferenceChange }) {
    const [preference, setPreferenceState] = React.useState(() => {
        if (typeof window === 'undefined')
            return 'system';
        try {
            const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
            return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
        }
        catch {
            return 'system';
        }
    });
    const [theme, setTheme] = React.useState(() => {
        if (typeof document === 'undefined')
            return 'light';
        const attr = document.documentElement.getAttribute('data-theme');
        return attr === 'dark' ? 'dark' : 'light';
    });
    const setPreference = React.useCallback((next) => {
        setPreferenceState(next);
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, next);
        }
        catch {
            // Storage unavailable — the in-memory preference still applies this session.
        }
        const resolved = applyStoredTheme();
        setTheme(resolved);
        onPreferenceChange?.(next);
    }, [onPreferenceChange]);
    // §8.8: "Never an automatic runtime flip mid-session based on system time or ambient
    // light" — this listener only re-resolves when the OS signal changes AND the stored
    // preference is `system`, which is the explicit "fresh OS-preference read" case, not
    // a mid-session flip driven by anything else.
    React.useEffect(() => {
        if (preference !== 'system')
            return undefined;
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => setTheme(applyStoredTheme());
        media.addEventListener('change', handler);
        return () => media.removeEventListener('change', handler);
    }, [preference]);
    const value = React.useMemo(() => ({ theme, preference, setPreference }), [theme, preference, setPreference]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
}
export function useTheme() {
    const context = React.useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a <ThemeProvider> (internal portal only — see PublicThemeScope for the public app).');
    }
    return context;
}
/**
 * Public portal (§8.1): "stays light-only". This is not a ThemeProvider with dark
 * disabled — it is a structurally different, minimal component with no localStorage
 * read, no `prefers-color-scheme` listener, and no dark token CSS dependency, so a
 * bundler cannot pull dark-mode code into the public chunk just because a component
 * imports "the theme provider".
 */
export function PublicThemeScope({ children }) {
    React.useEffect(() => {
        document.documentElement.setAttribute('data-theme', 'light');
    }, []);
    return _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=theme-context.js.map